




































































































































































































































































import Vue from 'vue'
import Modal from '@/components/Modal'
import { Select, Option } from 'element-ui'
import { Stream, StreamType } from '@/types/streams'
import { Team } from '@/types/teams'
import { required, minLength, between } from 'vuelidate/lib/validators'
import { StreamUrl } from '@/classes/streamurl'
import { TranscodingRegion } from '@/types/admin'
import { KeyDict } from '@/types'
import './AddEditStream.scss'
import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import { Feature } from '@/featureFlags/featureFlags'
import { DropdownOption } from '@/types/dropdownOption'

interface RegionSelection {
  value: string,
  name: string
}

export default Vue.extend({
  name: 'AddEditStream',
  props: {
    inputStream: {
      type: Object as () => Stream,
      required: true
    },
    showModal: {
      type: Boolean,
      required: true,
      default: false
    },
    teamStreams: {
      type: Object as () => KeyDict<Stream>
    },
    team: {
      type: String
    },
    streams: {
      type: Object
    }
  },
  computed: {
    modalTitle (): string {
      return this.inputStream.hasOwnProperty('name') ? 'Edit Stream' : 'Create a new stream'
    },
    isOps (): boolean {
      return this.$can(AbilityAction.Do, AbilitySubject.Ops)
    },
    isSelfServe (): boolean {
      return this.$hasFeature(Feature.SELF_SERVE)
    },
    modalButton (): string {
      return 'submit'
    },
    teamDict (): Team {
      return this.$store.direct.state.team.team
    },
    reencodeTooltip (): string {
      let tooltip = 'By default the stream has the same bitrate and resolution as the cast. '
      tooltip += '<br/>When enabling reencode you can create lower bitrate and resolution streams. '
      tooltip += '<br/>Use this when your destination (eg YouTube) has a maximum recommended bandwidth.'
      return tooltip
    },
    transcodingregions (): KeyDict<TranscodingRegion> {
      return this.$store.direct.state.admin.transcodingregions
    },
    regions (): RegionSelection[] {
      const regions: RegionSelection[] = []
      // @ts-ignore
      Object.values(this.transcodingregions).forEach((region: TranscodingRegion) => {
        // If authentication is enabled, then we also require a nimble server to be available.
        if (region.rtmpserver && (!this.authEnabled || region.nimbleserver)) {
          regions.push({
            name: region.name,
            value: region.name
          })
        }
      })
      return regions
    },
    teamHasAuth (): boolean {
      // FIXME: Due to the use of computed and props in the `editStream` and `streamurl` validations, Vetur seems to get
      //        confused. And that results in it showing errors here like:
      //        "Property 'teamHasAuth' does not exist on type CombinedVueInstance<..."
      // @ts-ignore
      const team = this.teamDict
      // TODO: Determine stream type based on url (and srt option?).
      const streamType = 'rtmp'
      if (!streamType) return false

      const login = team.streams_auth?.[streamType].login
      const pending = team.streams_auth?.[streamType].pending ?? false
      const salt = team.streams_auth?.[streamType].salt
      return login !== undefined && !pending && salt !== undefined
    },
    authError (): string {
      if (this.inputStream.id === undefined) return ''
      // The `inputStream` object doesn't seem to react to changes in the `error` property. Retrieving the stream
      // via the `streams` dict, does seem to be reactive.
      const currentStream = this.streams[this.inputStream.id]
      const errorMsg = currentStream.auth_info?.error ?? ''
      if (errorMsg === '') return ''
      // @ts-ignore See teamHasAuth
      return this.isOps ? `Stream auth error: ${errorMsg}` : 'Error while processing stream authentication changes'
    },
    authTitleMessage (): string {
      // @ts-ignore See teamHasAuth
      if (!this.teamHasAuth) return 'Stream authentication must correctly be set up in team settings.'
      if (this.authCheckboxDisabled) return 'Selected region does not support authenticated streams.'
      return ''
    },
    showRepublish (): boolean {
      // @ts-ignore See teamHasAuth
      return this.isOps && this.authEnabled && this.editStream.type === StreamType.Broadcast
    },
    showRegionSelect (): boolean {
      // @ts-ignore See teamHasAuth
      return this.editStream.type === StreamType.Broadcast && (!this.isOps || this.authEnabled)
    },
    disableIngestUrl (): boolean {
      // @ts-ignore See teamHasAuth
      return this.editStream.type !== StreamType.Output && (!this.isOps || this.authEnabled)
    },
    qualities (): DropdownOption[] {
      return this.$store.direct.getters.team.allowedStreamQualityDropdownOptions
    },
    framerates (): DropdownOption[] {
      return this.$store.direct.getters.team.allowedStreamFramerateDropdownOptions
    }
  },
  data: () => ({
    submitStatus: '',
    mode: 'create',
    streamurl: '',
    streamkey: '',
    originalStreamUrl: '',
    selectedRegion: '',
    srtEnabled: false,
    authEnabled: false,
    authCheckboxDisabled: true,
    authSubmitError: '',
    opsRepublishUrl: '',
    editStream: {
      name: '',
      srcurl: '',
      event_id: '',
      start_date: new Date(),
      end_date: new Date(),
      preview_scenes: {},
      type: StreamType.Output,
      leftrightsplit: false,
      deleted: false,
      reencode: false,
      resolution: '0720p006000',
      framerate: '0',
      interlaced: false,
      gop_size: 4,
      auth_info: undefined
    } as Stream
  }),
  validations: {
    editStream: {
      name: {
        required,
        minLength: minLength(2),
        doesNotExists: function (value) {
          // @ts-ignore
          return this.mode === 'edit' || Object.values(this.streams).filter(stream => stream.name === value).length === 0
        }
      },
      type: {
        required
      },
      gop_size: {
        required,
        between: between(1,6)
      }
    },
    streamurl: {
      required,
      minLength: minLength(2),
      isrtmp: function (value) {
        if (!(value.startsWith('rtmp://') || (this.editStream.type === StreamType.Output && value.startsWith('rtmps://')))) {
          return false
        }
        // @ts-ignore
        const url = this.removeTrailingSlash(value)
        // TODO: Add support for appname with slashes
        const args = url.split('/')
        const allowedLength = [4]
        if (value.includes('media.azure.net')) allowedLength.push(5)
        if (!allowedLength.includes(args.length)) {
          return false
        }
        if (args[2].length > 3 && args[3].length >= 1) {
          return true
        }
        return false
      },
      mustNotBeKiswe: function (value) {
        if (this.editStream.type === StreamType.Broadcast) {
          return true
        }
        if (this.originalStreamUrl === value) {
          return true
        }
        // @ts-ignore
        return this.isOps ? true : !value.includes('kiswe.com')
      }
    },
    streamkey: {
      required,
      minLength: minLength(2),
      notReservedNames: function (value) {
        // @ts-ignore
        if (this.editStream.type === StreamType.Output) {
          return true
        }
        const parts = value.split('_')
        if (parts.length === 3 && ['rtmp', 'rtsp'].includes(parts[1])) {
          return false
        }
        if (parts.length === 4 && (parts[2] === 'rtsp' || parts[3] === 'srt')) {
          return false
        }
        return true
      }
    }
  },
  watch: {
    selectedRegion () {
      // @ts-ignore See teamHasAuth
      const region = this.getSelectedRegion()
      if (region === undefined || !region.nimbleserver) {
        this.authCheckboxDisabled = true
        if (this.authEnabled) {
          console.warn('A region with no nimble server was selected while authentication was enabled. Forcing it to disabled.')
          this.authEnabled = false
        }
      } else {
        this.authCheckboxDisabled = false
      }
      // @ts-ignore See teamHasAuth
      this.streamurl = this.constructUrl(region)
      let found = true
      let key = ''
      while (found) {
        // @ts-ignore
        key = this.getNewKey()
        // @ts-ignore
        found = Object.values(this.streams).find((stream: Stream) => stream.srcurl.includes(key)) !== undefined
      }
      this.streamkey = key
      // @ts-ignore See teamHasAuth
      if (this.isOps && this.authEnabled) this.opsRepublishUrl = StreamUrl.stripStreamKey(this.getAuthRepublishUrl())
    },
    srtEnabled (value) {
      if (value) {
        this.editStream.srt_info = {
          nimbleserver: '',
          port: 0,
          mode: 'listen'
        }
      } else {
        this.editStream.srt_info = undefined
      }
    },
    authEnabled (): void {
      // If we set `authEnabled` in `created()` (in edit mode), there won't be any region selected yet. So we don't
      // update `streamurl` in that case, as it would be set to ''.
      if (this.selectedRegion === '') return
      // @ts-ignore See teamHasAuth
      this.streamurl = this.constructUrl(this.getSelectedRegion())
    }
  },
  created () {
    if (this.inputStream.hasOwnProperty('name')) {
      this.editStream = {...this.editStream, ...this.inputStream}
      this.mode = 'edit'
      const strurl = new StreamUrl(this.editStream.auth_info?.ingest_url ?? this.editStream.srcurl)
      this.srtEnabled = this.editStream.srt_info !== undefined
      if (this.srtEnabled) {
        // FIXME needs to be done properly -> needs to happen after the watch
        setTimeout(() => { this.editStream.srt_info = this.inputStream.srt_info }, 100)
      }
      this.streamurl = strurl.streamurl || ''
      this.originalStreamUrl = this.streamurl
      this.streamkey = strurl.streamkey || ''
      this.editStream.interlaced = this.inputStream.rate !==  undefined ? this.inputStream.rate.interlaced : false
      this.editStream.gop_size = (this.inputStream.rate !== undefined && this.inputStream.rate.gop_size !== undefined) ? this.inputStream.rate.gop_size : 4

      this.authEnabled = this.editStream.auth_info?.ingest_url !== undefined
      // @ts-ignore See teamHasAuth
      if (this.isOps && this.authEnabled) {
        this.opsRepublishUrl = StreamUrl.stripStreamKey(this.editStream.srcurl)
      }
    }

    this.editStream.team = this.team
    this.editStream.team_id = this.team

    // Allow ops to change the authentication toggle without having to select a region.
    // @ts-ignore See teamHasAuth
    if (this.isOps) this.authCheckboxDisabled = false
  },
  methods: {
    removeTrailingSlash (url: string): string {
      if (url.endsWith('/')) {
        return url.slice(0, url.length - 1)
      }
      return url
    },
    getNewKey (): string {
      // @ts-ignore
      const team_name = this.teamDict.name.replace(/\W/g, '').toLowerCase()
      const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let key = ''
      for (let i = 0; i < 6; i++) {
        key += possible.charAt(Math.floor(Math.random() * possible.length))
      }
      return `${team_name}_${key}`
    },
    formSubmitted (): void {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.submitStatus = 'ERROR'
      } else {
        this.authSubmitError = ''
        const strurl = new StreamUrl()
        strurl.fromUrlAndKey(this.streamurl, this.streamkey)
        if (this.authEnabled && this.editStream.type === StreamType.Broadcast) {
          // @ts-ignore See teamHasAuth
          const republishUrl = this.getAuthRepublishUrl()
          if (republishUrl === '') {
            this.submitStatus = 'ERROR'
            this.authSubmitError = 'Failed to construct auth info'
            return
          }
          const rpstrurl = new StreamUrl()
          rpstrurl.fromUrlAndKey(republishUrl, this.streamkey)
          // TODO: Get correct stream type, don't just hard-code it.
          const streamType = 'rtmp'
          this.editStream.srcurl = rpstrurl.url
          this.editStream.auth_info = {
            type: streamType,
            ingest_url: strurl.url
          }
        } else {
          this.editStream.srcurl = strurl.url
          this.editStream.auth_info = undefined
        }
        this.$v.$reset()
        // @ts-ignore
        this.updateRate()
        if (this.mode === 'create') {
          this.$emit('createStream', this.editStream)
        } else if (this.mode === 'edit') {
          this.$emit('updateStream', this.editStream)
        }
      }
    },
    hideModal (): void {
      this.$emit('closeModal')
    },
    updateRate (): void {
      if (!this.editStream.reencode && this.editStream.rate !== undefined) {
        delete this.editStream.rate
      } else if (this.editStream.reencode) {
        this.editStream.rate = {}
        if (this.editStream.framerate !== '0') {
          this.editStream.rate.fps = this.editStream.framerate
        }
        if (this.editStream.resolution) {
          if (this.editStream.resolution.indexOf('720p') !== -1) {
            this.editStream.rate.width = 1280
            this.editStream.rate.height = 720
          }
          if (this.editStream.resolution.indexOf('1080p') !== -1) {
            this.editStream.rate.width = 1920
            this.editStream.rate.height = 1080
          }
          this.editStream.rate.video_bitrate = parseInt(this.editStream.resolution.split('p')[1])
          this.editStream.rate.interlaced = this.editStream.interlaced
        }
        this.editStream.rate.gop_size = this.editStream.gop_size
      }
    },
    constructUrl (region: TranscodingRegion|undefined): string {
      if (region === undefined) {
        console.error('Cannot construct auth url without region.')
        return ''
      }
      if (this.authEnabled) {
        if (!region.nimbleserver) {
          console.error('Cannot construct auth url for region without nimbleserver.')
          return ''
        }
        // TODO: Get correct stream type, don't just hard-code it.
        const streamType = 'rtmp'
        return `${streamType}://${region.nimbleserver}:1936/${streamType}-${this.team}`
      } else {
        if (!region.rtmpserver) {
          console.error('Cannot construct auth url for region without rtmpserver.')
          return ''
        }
        return `rtmp://${region.rtmpserver}/live`
      }
    },
    getAuthRepublishUrl (): string {
      // @ts-ignore See teamHasAuth
      const region = this.getSelectedRegion()
      if (region === undefined || !region.rtmpserver) {
        console.error(`Currently selected region (${this.selectedRegion}) has no rtmpserver`)
        return ''
      }
      // TODO: Get correct stream type, don't just hard-code it.
      const streamType = 'rtmp'
      // @ts-ignore See teamHasAuth
      const salt = this.teamDict.streams_auth?.[streamType].salt
      if (!salt) {
        console.error('Cant construct republish url if team has no salt information.')
        return ''
      }
      return `${streamType}://${region.rtmpserver}/live/${salt}`
    },
    getSelectedRegion (): TranscodingRegion|undefined {
      // @ts-ignore See teamHasAuth
      return Object.values(this.transcodingregions).find(region => region.name === this.selectedRegion)
    },
    showDimissAuthErrorConfirm (): void {
      this.$msgbox({
        message: this.$createElement('p', undefined, [
          this.$createElement('p', undefined, 'Are you sure you want to dismiss the authentication error for this stream?'),
          this.$createElement('p', undefined, 'This will clear the error state of the stream authentication, but it will not resolve the error itself.'),
          this.$createElement('br'),
          this.$createElement('p', undefined, 'Only dismiss the error in case the underlying problem has been resolved!')
        ]),
        title: 'Warning',
        confirmButtonText: 'Dismiss Error',
        cancelButtonText: 'Cancel',
        showCancelButton: true,
        type: 'warning'
      }).then(async () => {
        const userId = this.$store.direct.getters.user.currentUserId
        this.$store.direct.dispatch.logs.addLog({ message: `Dismissing auth error for stream id: ${this.inputStream.id} by user id: ${userId}`, level: 'info' })
        try {
          await this.$store.direct.dispatch.streams.clearStreamAuthError(this.inputStream.id ?? '')
        } catch (error) {
          console.error('Failed to clear stream error state:', error)
          this.$message.error('Failed to clear stream error state')
        }
      }).catch(() => {
      })
    }
  },
  components: {
    Modal,
    'el-select': Select,
    'el-option': Option,
  }
})
